import ClientApplication from 'api/application/interfaces/ClientApplication'
import { SubmitApplicationDTO } from 'api/application/postApplication'
import { Disclosure } from 'api/lender/getLenderDisclosures'
import Decimal from 'decimal.js'
import SourceAction from 'state/enums/SourceAction'
import SourceClient from 'state/enums/SourceClient'
import SourceSubclient from 'state/enums/SourceSubclient'
import getPortal from 'utils/getPortal'

import { INCOME_INTERVAL } from '../../../form/fields/employmentFields'
import { parseToDecimal } from '../../../utils/toNumber'
import { FormValues } from '../../FormBuilder'
import getCaptiveIdentifier from '../getCaptiveIdentifier'
import { tradeInFieldNames as tradeInFieldNamesRV } from '../RVApplication/rvApplicationFields'
import {
    buildOtherIncomeFields,
    buildResidentialData,
    addDisclosuresToDTO,
} from '../sharedDTOHelpers'
import {
    applicantFieldNames,
    coapplicantFieldNames,
    otherFieldNames,
    hiddenFormValues,
    isApplicationJointFunc,
    isCoapplicantAddressSameFunc,
} from '../sharedInstructions'
import {
    buildCoapplicantWisconsinSpouseData,
    buildPrimaryWisconsinSpouseData,
} from '../wisconsinInstructions'
import { tradeInFieldNames as tradeInFieldNamesAuto } from './automobileApplicationFields'

type PersonalDataFields = {
    first_name: string
    middle_initial?: string
    last_name: string
    suffix: string
    citizenship_status: string
    social_security_number: string
    dob: string
    residential_status: string
    home_phone: string
    mobile_phone: string
    work_phone: string
    residential_years: string
    residential_months: string
    monthly_housing_payment: string
    job_title: string
    employment_status: string
    employer_name: string
    employer_years: string
    employer_months: string
    employer_monthly_gross: string
    employer_gross_amount: string
    employer_gross_interval: string
    email_address: string
}

function calculateMonthlyGross(
    incomeAmount: string,
    incomeInterval: string,
): string {
    const amountNumber = parseToDecimal(incomeAmount)
    let monthlyNumber: Decimal

    switch (incomeInterval) {
        case INCOME_INTERVAL.yearly.value:
            monthlyNumber = amountNumber.dividedBy(12)
            break
        case INCOME_INTERVAL.monthly.value:
            monthlyNumber = amountNumber
            break
        case INCOME_INTERVAL.biweekly.value:
            monthlyNumber = amountNumber.times(26).dividedBy(12)
            break
        case INCOME_INTERVAL.semiMonthly.value:
            monthlyNumber = amountNumber.times(2)
            break
        case INCOME_INTERVAL.weekly.value:
            monthlyNumber = amountNumber.times(52).dividedBy(12)
            break
        default:
            monthlyNumber = Decimal(NaN)
            break
    }

    return monthlyNumber.toDP(0).toString()
}

function buildPersonalData(
    values: FormValues,
    fieldNames: typeof applicantFieldNames | typeof coapplicantFieldNames,
): PersonalDataFields {
    return {
        first_name: values[fieldNames.firstName].toUpperCase(),
        middle_initial: values[fieldNames.middleInitial]?.toUpperCase(),
        last_name: values[fieldNames.lastName].toUpperCase(),
        suffix: values[fieldNames.suffix],
        citizenship_status: values[fieldNames.citizenshipStatus],
        social_security_number: values[fieldNames.ssn],
        dob: values[fieldNames.dateOfBirth].replaceAll(/\s/g, ''),
        residential_status: values[fieldNames.residenceStatus],
        home_phone: values[fieldNames.homePhone],
        mobile_phone: values[fieldNames.mobilePhone],
        work_phone:
            values[fieldNames.workPhone] || values[fieldNames.mobilePhone],
        residential_years: values[fieldNames.residentialYears],
        residential_months: values[fieldNames.residentialMonths],
        monthly_housing_payment: values[fieldNames.monthlyHousingPayment],
        job_title:
            values[fieldNames.jobTitle] || values[fieldNames.employmentStatus],
        employment_status: values[fieldNames.employmentStatus],
        employer_name: values[fieldNames.employerName],
        employer_years: values[fieldNames.employerYears],
        employer_months: values[fieldNames.employerMonths],
        employer_monthly_gross: calculateMonthlyGross(
            values[fieldNames.employerGrossAmount],
            values[fieldNames.employerGrossInterval],
        ),
        employer_gross_amount: values[fieldNames.employerGrossAmount],
        employer_gross_interval: values[fieldNames.employerGrossInterval],
        email_address: values[fieldNames.email],
    }
}

function buildPrefixedPersonalData(
    values: FormValues,
    fieldNames: typeof applicantFieldNames | typeof coapplicantFieldNames,
    prefix: string,
): Record<string, string | undefined> {
    const personalData = buildPersonalData(values, fieldNames)

    return Object.entries(personalData).reduce(
        (acc, [key, value]) => {
            acc[`${prefix}${key}`] = value

            return acc
        },
        {} as Record<string, string | undefined>,
    )
}

export default function createPostApplicationDTO(
    values: FormValues,
    isCopy = false,
    parentId = 0,
    sourceClient: SourceClient = SourceClient.OCTANE_EXTENSION,
    sourceSubclient: SourceSubclient | undefined = undefined,
    disclosures: Disclosure[] | undefined = undefined,
): SubmitApplicationDTO {
    const otherIncomeFields = buildOtherIncomeFields(
        values,
        applicantFieldNames,
        calculateMonthlyGross,
    )

    const applicantPersonalData = buildPersonalData(values, applicantFieldNames)

    const dtoBase = {
        source_client: sourceClient,
        source_subclient: sourceSubclient,
        source_action: isCopy
            ? SourceAction.COPY
            : SourceAction.FORM_SUBMISSION,
        group: values[hiddenFormValues.group],
        application_type: isApplicationJointFunc(values) ? 'J' : 'I',
        finance_manager: values[otherFieldNames.financeManager],
        ...applicantPersonalData,
        ...buildResidentialData(applicantFieldNames, values),
        ...otherIncomeFields,
        trade_in_intent: values[tradeInFieldNamesAuto.intent],
        dealer_trade_in_intent: values[tradeInFieldNamesRV.dealerIntent],
        portal: getPortal(),
        captive_identifier: getCaptiveIdentifier(),
        wisconsin_spouse: buildPrimaryWisconsinSpouseData(values),
    }

    let dto: SubmitApplicationDTO = dtoBase as SubmitApplicationDTO

    if (isCopy && parentId !== 0) {
        dto = {
            ...dto,
            parent: parentId,
        }
    }

    dto = addDisclosuresToDTO(dto, values, disclosures)

    if (!isApplicationJointFunc(values)) {
        return dto
    }

    const coapplicantResidentialData = isCoapplicantAddressSameFunc(values)
        ? buildResidentialData(applicantFieldNames, values, 'coapplicant_')
        : buildResidentialData(coapplicantFieldNames, values, 'coapplicant_')

    const coapplicantOtherIncomeFields = buildOtherIncomeFields(
        values,
        coapplicantFieldNames,
        calculateMonthlyGross,
        'coapplicant_',
    )

    const coapplicantPersonalData = buildPrefixedPersonalData(
        values,
        coapplicantFieldNames,
        'coapplicant_',
    )

    return {
        ...dto,
        ...coapplicantResidentialData,
        ...coapplicantPersonalData,
        coapplicant_relationship: values[otherFieldNames.relationOfCoapplicant],
        ...coapplicantOtherIncomeFields,
        coapplicant_wisconsin_spouse:
            buildCoapplicantWisconsinSpouseData(values),
    }
}

export function createPostApplicationDTOFromClientApplication(
    application: ClientApplication,
): SubmitApplicationDTO {
    const {
        id,
        lender_name,
        progress,
        status,
        uuid,
        source_action,
        parent,
        ...postApplication
    } = application

    return {
        ...postApplication,
        portal: getPortal(),
    } as unknown as SubmitApplicationDTO
}

export const testExports = {
    calculateMonthlyGross,
}
